import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CreditCard, Download, FileText, Loader2, ReceiptText, Wallet } from "lucide-react";
import { useState } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/App";
import { EditPartyDialog } from "./EditPartyDialog";
import { SubscribeButton } from "./components/SubscribeButton";
import { SavingsTransactionDialog } from "./components/SavingsTransactionDialog";
import { ReverseSavingsTransactionDialog } from "./components/ReverseSavingsTransactionDialog";
import { RefundCreditLotDialog } from "./components/RefundCreditLotDialog";
import { CreditLotEntriesDialog } from "./components/CreditLotEntriesDialog";
import { ConvertCreditDialog } from "./components/ConvertCreditDialog";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { cn } from "@/lib/utils";
import { queryKeys } from "@/lib/query-keys";
import { useParty } from "@/hooks/queries/useParty";
import { usePartyCreditBalance } from "@/hooks/queries/usePartyCreditBalance";
import { usePartyCreditLots } from "@/hooks/queries/usePartyCreditLots";
import { usePartyHistory } from "@/hooks/queries/usePartyHistory";
import { useCreances } from "@/hooks/queries/useCreances";
import { usePartySavingsAccount } from "@/hooks/queries/usePartySavingsAccount";
import { usePartySavingsTransactions } from "@/hooks/queries/usePartySavingsTransactions";
import { useTogglePartyActivationMutation } from "@/hooks/mutations/useTogglePartyActivation";
import { PartyProfileHeader } from "./components/PartyProfileHeader";
import { PartyKpiCard } from "./components/PartyKpiCard";
import { partyApi } from "@/lib/party.api";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "sonner";
import type { SavingsAccount, SavingsTransaction } from "@/lib/savings.api";
import { savingsApi } from "@/lib/savings.api";
import { ReassignResponsibleAgentDialog } from "@/components/ReassignResponsibleAgentDialog";
import { useAttributionReassignmentFlag } from "@/hooks/queries/useAttributionReassignmentFlag";
import { canUseReassignment } from "@/lib/reassignment";
import { DetailError, QuerySection } from "@/components/QueryState";
import { parseRecordId, isValidRecordId } from "@/lib/route-id";

const NATURE_LABELS: Record<string, string> = { deposit: "Dépôt", withdraw: "Retrait" };

// 2026-09-23 fix: the inscription-fee deposit+withdraw pair used to render
// as plain "Dépôt"/"Retrait", indistinguishable from a real client movement
// (confirmed source of confusion during a client smoke test). There's no
// dedicated flag on the transaction row for this, so it's detected
// client-side from the one combination that's specific to it: a deposit and
// a withdrawal of the exact same amount (= the account's own inscription
// fee), recorded at the exact same instant (both legs are inserted in the
// same transaction, sharing one `now` — see savings.service.ts /
// savings-conversion.service.ts). Deliberately conservative: any real
// deposit/withdrawal pair would need to coincidentally match all three
// conditions to be mislabeled, which doesn't happen in practice.
function isInscriptionFeePairLeg(
  t: SavingsTransaction,
  all: SavingsTransaction[],
  account: SavingsAccount | null,
): boolean {
  if (!account) return false;
  const feeAmount = parseFloat(account.inscriptionFeeAmount);
  if (parseFloat(t.totalAmount) !== feeAmount) return false;

  return all.some(
    (other) =>
      other.id !== t.id &&
      other.nature !== t.nature &&
      other.recordedAt === t.recordedAt &&
      parseFloat(other.totalAmount) === feeAmount,
  );
}

function transactionLabel(
  t: SavingsTransaction,
  all: SavingsTransaction[],
  account: SavingsAccount | null,
): string {
  if (isInscriptionFeePairLeg(t, all, account)) return "Frais d'inscription épargne";
  return NATURE_LABELS[t.nature];
}

function money(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const numeric = typeof value === "string" ? parseFloat(value) : value;
  return `${numeric.toLocaleString("fr-FR")} XAF`;
}

export default function PartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  return <PartyDetails key={id} />;
}

function PartyDetails() {
  const { id } = useParams<{ id: string }>();
  const partyId = parseRecordId(id);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [commercialPage, setCommercialPage] = useState(1);

  const partyQuery = useParty(partyId);
  const { data: party, isLoading } = partyQuery;
  const balanceQuery = usePartyCreditBalance(partyId);
  const { data: creditBalanceData } = balanceQuery;
  const creditBalance = creditBalanceData?.balance ?? null;
  const lotsQuery = usePartyCreditLots(partyId);
  const { data: creditLots = [] } = lotsQuery;
  const historyQuery = usePartyHistory(partyId, commercialPage);
  const { data: history = null } = historyQuery;
  const recentHistoryQuery = usePartyHistory(partyId);
  const creancesQuery = useCreances({ partyId });
  const { data: creances = [] } = creancesQuery;
  // Separate from the main queries — a party without a savings account is the
  // normal case (404), not an error the page should block on.
  const savingsQuery = usePartySavingsAccount(partyId);
  const { data: savingsAccount = null } = savingsQuery;
  const transactionsQuery = usePartySavingsTransactions(savingsAccount?.id);
  const { data: savingsTransactions = [] } = transactionsQuery;
  const { data: reassignmentFlag } = useAttributionReassignmentFlag();

  const toggleActivationMutation = useTogglePartyActivationMutation(partyId);
  const deleteMutation = useMutation({
    mutationFn: () => partyApi.remove(partyId),
    onSuccess: () => {
      toast.success("Partie supprimée.");
      navigate("/parties");
    },
    onSettled: () => setConfirmDelete(false),
  });

  // EditPartyDialog/SavingsTransactionDialog/SubscribeButton/
  // ReverseSavingsTransactionDialog aren't on useMutation yet (out of scope
  // for this pass), so they still need this explicit invalidate after their
  // own plain API calls. Same scope as the toggle-activation mutation above.
  function handleReload() {
    queryClient.invalidateQueries({ queryKey: queryKeys.party(partyId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.credits(partyId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.creditLots(partyId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.partyHistory(partyId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.creances({ partyId }) });
    queryClient.invalidateQueries({ queryKey: queryKeys.savings(partyId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.savingsTransactions() });
  }

  usePageHeader({
    title: party?.fullName ?? "Partie",
    backTo: "/parties",
    helpTopic: "parties",
    guide: {
      steps: [
        "Cliquez sur « Modifier » pour changer les informations de la partie.",
        "« Désactiver »/« Activer » ne supprime jamais la partie — son historique reste intact.",
        "L'historique commercial et épargne se trouve plus bas sur la page, en deux sections séparées.",
      ],
    },
  });

  function handleToggleActivation() {
    if (!party) return;
    toggleActivationMutation.mutate(!party.active);
  }

  if (!isValidRecordId(partyId) || partyQuery.isError) {
    return (
      <DetailError
        invalid={!isValidRecordId(partyId)}
        error={partyQuery.error}
        backTo="/parties"
        onRetry={() => void partyQuery.refetch()}
      />
    );
  }
  if (isLoading || !party) {
    return <Loader2 className="animate-spin text-subtle" size={18} />;
  }

  const canManage = user && ["manager", "admin", "super_admin"].includes(user.role);
  const canReverse = user && ["admin", "super_admin"].includes(user.role);
  const canReassign = canUseReassignment(user?.role, reassignmentFlag);
  const openLots = creditLots.filter((l) => !l.convertedAt && parseFloat(l.remainingAmount) > 0);
  const creancesTotal = creances.reduce(
    (sum, c) => sum + parseFloat(c.principalDue) + parseFloat(c.penaltyDue),
    0,
  );
  const overdueCount = creances.filter((c) => c.isOverdue).length;
  const recentCommercial = recentHistoryQuery.data?.commercial.data.slice(0, 3) ?? [];
  const recentSavings = savingsTransactions.slice(0, 3);

  return (
    <div>
      <PartyProfileHeader
        party={party}
        canManage={!!canManage}
        toggling={toggleActivationMutation.isPending}
        onEdit={() => setEditing(true)}
        onToggleActivation={handleToggleActivation}
        canDelete={user?.role === "super_admin"}
        onDelete={() => setConfirmDelete(true)}
      />

      {/* Three balances — never merged, per M3 spec */}
      <div className="grid grid-cols-1 gap-3 my-6 md:grid-cols-3">
        <QuerySection query={creancesQuery} label="Créances">
          <PartyKpiCard
            label="Créances dues"
            value={creances.length === 0 ? "0 XAF" : money(creancesTotal)}
            detail={
              creances.length === 0
                ? "Aucune créance en cours"
                : `${creances.length} échéance${creances.length > 1 ? "s" : ""}${overdueCount > 0 ? ` · ${overdueCount} en retard` : ""}`
            }
            icon={ReceiptText}
            tone={creancesTotal > 0 ? "danger" : "neutral"}
          />
        </QuerySection>
        <QuerySection query={balanceQuery} label="Crédit">
          <QuerySection query={lotsQuery} label="Lots de crédit">
            <PartyKpiCard
              label="Crédit / Avoir"
              value={money(creditBalance)}
              detail={
                openLots.length > 0
                  ? `${openLots.length} lot${openLots.length > 1 ? "s" : ""} ouvert${openLots.length > 1 ? "s" : ""}`
                  : "Aucun lot ouvert"
              }
              icon={CreditCard}
              tone="gold"
            />
          </QuerySection>
        </QuerySection>
        <QuerySection query={savingsQuery} label="Épargne">
          <PartyKpiCard
            label="Épargne voyage"
            value={savingsAccount ? money(savingsAccount.balance) : "—"}
            detail={
              savingsAccount
                ? savingsAccount.subscriptionSource === "direct"
                  ? "Souscription directe"
                  : "Conversion crédit"
                : "Aucun compte ouvert"
            }
            icon={Wallet}
            tone={savingsAccount ? "gold" : "neutral"}
          />
        </QuerySection>
      </div>

      {savingsQuery.isSuccess && savingsAccount && (
        <div className="flex justify-end mb-4">
          <SavingsTransactionDialog account={savingsAccount} onRecorded={handleReload} />
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="credit">Lots de crédit</TabsTrigger>
          <TabsTrigger value="commercial">Historique commercial</TabsTrigger>
          <TabsTrigger value="epargne">Historique épargne</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div>
                  <p className="text-[12px] font-semibold text-foreground">
                    Activité commerciale récente
                  </p>
                  <p className="text-[10.5px] text-muted-foreground">
                    Factures et proformas liés à cette partie
                  </p>
                </div>
                <FileText size={15} className="text-subtle" />
              </div>
              <QuerySection query={recentHistoryQuery} label="Historique commercial">
                {recentCommercial.length === 0 ? (
                  <p className="px-4 py-6 text-[12px] text-muted-foreground">
                    Aucun document commercial pour cette partie.
                  </p>
                ) : (
                  <div className="divide-y divide-neutral-100">
                    {recentCommercial.map((entry) => (
                      <Link
                        key={`${entry.docType}-${entry.id}`}
                        to={
                          entry.docType === "invoice"
                            ? `/invoices/${entry.id}`
                            : `/proformas/${entry.id}`
                        }
                        className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-muted"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-[12px] font-medium text-body">
                            {entry.docType === "invoice" ? "Facture" : "Proforma"}{" "}
                            {entry.number ?? `#${entry.id}`}
                          </p>
                          <p className="text-[10.5px] text-muted-foreground">
                            {new Date(entry.date).toLocaleDateString("fr-FR")} · {entry.status}
                          </p>
                        </div>
                        <p className="shrink-0 text-[12.5px] font-semibold text-foreground">
                          {money(entry.amount)}
                        </p>
                      </Link>
                    ))}
                  </div>
                )}
              </QuerySection>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div>
                  <p className="text-[12px] font-semibold text-foreground">Épargne récente</p>
                  <p className="text-[10.5px] text-muted-foreground">
                    Derniers mouvements du compte voyage
                  </p>
                </div>
                <Wallet size={15} className="text-subtle" />
              </div>
              <QuerySection query={savingsQuery} label="Épargne">
                {!savingsAccount ? (
                  <div className="px-4 py-6">
                    <p className="text-[12px] text-muted-foreground">
                      Aucun compte épargne pour cette partie.
                    </p>
                    {canManage && (
                      <div className="mt-3">
                        <SubscribeButton partyId={partyId} onSubscribed={handleReload} />
                      </div>
                    )}
                  </div>
                ) : (
                  <QuerySection query={transactionsQuery} label="Mouvements épargne">
                    {recentSavings.length === 0 ? (
                      <p className="px-4 py-6 text-[12px] text-muted-foreground">
                        Aucun mouvement pour ce compte.
                      </p>
                    ) : (
                      <div className="divide-y divide-neutral-100">
                        {recentSavings.map((t) => (
                          <div
                            key={t.id}
                            className="flex items-center justify-between gap-3 px-4 py-3"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-[12px] font-medium text-body">
                                {transactionLabel(t, savingsTransactions, savingsAccount)}
                                {t.receiptNumber && (
                                  <span className="ml-1 text-[10.5px] text-muted-foreground">
                                    {t.receiptNumber}
                                  </span>
                                )}
                              </p>
                              <p className="text-[10.5px] text-muted-foreground">
                                {t.recordedAt
                                  ? new Date(t.recordedAt).toLocaleString("fr-FR")
                                  : "—"}
                              </p>
                            </div>
                            <p className="shrink-0 text-[12.5px] font-semibold text-foreground">
                              {money(t.totalAmount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </QuerySection>
                )}
              </QuerySection>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="credit">
          {balanceQuery.isSuccess && (creditBalance ?? 0) > 0 && canManage && (
            <div className="flex justify-end mb-3">
              <ConvertCreditDialog partyId={partyId} availableCredit={creditBalance ?? 0} />
            </div>
          )}
          <QuerySection query={lotsQuery} label="Lots de crédit">
            {creditLots.length === 0 ? (
              <p className="text-[12px] text-muted-foreground py-4">
                Aucun lot de crédit pour cette partie.
              </p>
            ) : (
              <div className="space-y-2">
                {creditLots.map((lot) => {
                  const remaining = parseFloat(lot.remainingAmount);
                  const isOpen = !lot.convertedAt && remaining > 0;
                  return (
                    <div
                      key={lot.id}
                      className="bg-card border border-border rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-2"
                    >
                      <div>
                        <p className="text-[12px] font-medium text-body">
                          {remaining.toLocaleString("fr-FR")} /{" "}
                          {parseFloat(lot.amount).toLocaleString("fr-FR")} XAF
                        </p>
                        <p className="text-[10.5px] text-muted-foreground">
                          Créé le {new Date(lot.createdAt).toLocaleDateString("fr-FR")} · Fenêtre
                          jusqu'au{" "}
                          {new Date(lot.decisionWindowExpiresAt).toLocaleDateString("fr-FR")}
                          {lot.convertedAt && " · Converti en épargne"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isOpen && (
                          <span className="text-[10.5px] text-success-text font-medium">
                            Ouvert
                          </span>
                        )}
                        <CreditLotEntriesDialog lotId={lot.id} />
                        {isOpen && canManage && (
                          <RefundCreditLotDialog
                            partyId={partyId}
                            lotId={lot.id}
                            remainingAmount={remaining}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </QuerySection>
        </TabsContent>

        <TabsContent value="commercial">
          <QuerySection query={historyQuery} label="Historique commercial">
            {!history || history.commercial.data.length === 0 ? (
              <p className="text-[12px] text-muted-foreground py-4">
                Aucun document commercial pour cette partie.
              </p>
            ) : (
              <div className="space-y-2 py-2">
                {history.commercial.data.map((entry) => (
                  <Link
                    key={`${entry.docType}-${entry.id}`}
                    to={
                      entry.docType === "invoice"
                        ? `/invoices/${entry.id}`
                        : `/proformas/${entry.id}`
                    }
                    className="bg-card border border-border rounded-lg px-4 py-3 flex items-center justify-between hover:border-brand-gold-dark transition-colors"
                  >
                    <div>
                      <p className="text-[12px] font-medium text-body">
                        {entry.docType === "invoice" ? "Facture" : "Proforma"}{" "}
                        {entry.number ?? `#${entry.id}`}
                      </p>
                      <p className="text-[10.5px] text-muted-foreground">
                        {new Date(entry.date).toLocaleDateString("fr-FR")} · {entry.status}
                      </p>
                    </div>
                    <p className="text-[13px] font-semibold text-body">
                      {parseFloat(entry.amount).toLocaleString("fr-FR")} XAF
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </QuerySection>
          <nav
            aria-label="Pagination de l’historique commercial"
            className="flex items-center gap-3 mt-4"
          >
            <Button
              variant="outline"
              disabled={commercialPage === 1 || historyQuery.isFetching}
              onClick={() => setCommercialPage((page) => page - 1)}
            >
              Précédent
            </Button>
            <span>
              Page {commercialPage}
              {history
                ? ` sur ${Math.max(1, Math.ceil(history.commercial.total / history.commercial.pageSize))}`
                : ""}
            </span>
            <Button
              variant="outline"
              disabled={
                !history ||
                historyQuery.isFetching ||
                commercialPage * history.commercial.pageSize >= history.commercial.total
              }
              onClick={() => setCommercialPage((page) => page + 1)}
            >
              Suivant
            </Button>
          </nav>
        </TabsContent>

        <TabsContent value="epargne">
          <QuerySection query={savingsQuery} label="Épargne">
            {!savingsAccount ? (
              <div className="py-4 space-y-3">
                <p className="text-[12px] text-muted-foreground">
                  Aucun compte épargne pour cette partie.
                </p>
                {canManage && <SubscribeButton partyId={partyId} onSubscribed={handleReload} />}
              </div>
            ) : (
              <QuerySection query={transactionsQuery} label="Mouvements épargne">
                {savingsTransactions.length === 0 ? (
                  <p className="text-[12px] text-muted-foreground py-4">
                    Aucun mouvement pour ce compte.
                  </p>
                ) : (
                  <div className="space-y-2 py-2">
                    {savingsTransactions.map((t) => (
                      <div
                        key={t.id}
                        className="bg-card border border-border rounded-lg px-4 py-3 flex items-center justify-between"
                      >
                        <div>
                          <p className="text-[12px] font-medium text-body">
                            {transactionLabel(t, savingsTransactions, savingsAccount)} —{" "}
                            {parseFloat(t.totalAmount).toLocaleString("fr-FR")} XAF
                            {t.reversalOfTransactionId && (
                              <span className="text-[10.5px] text-warning-text ml-2">
                                (contre-passation)
                              </span>
                            )}
                            {!t.agentId && !t.reversalOfTransactionId && (
                              <span
                                className="text-[10.5px] text-info-text ml-2"
                                title="Converti automatiquement depuis un lot de crédit expiré, pas un dépôt en espèces"
                              >
                                (converti)
                              </span>
                            )}
                          </p>
                          <p className="text-[10.5px] text-muted-foreground">
                            {t.recordedAt ? new Date(t.recordedAt).toLocaleString("fr-FR") : "—"}
                            {t.appliedToInvoiceId &&
                              ` · appliqué à la facture #${t.appliedToInvoiceId}`}
                            {t.receiptNumber && ` · ${t.receiptNumber}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {t.receiptNumber && (
                            <a
                              href={`/api/savings/transactions/${t.id}/receipt`}
                              target="_blank"
                              rel="noreferrer"
                              className={cn(buttonVariants({ variant: "ghost", size: "icon" }))}
                              title="Télécharger le reçu"
                            >
                              <Download size={13} />
                            </a>
                          )}
                          {canReverse && !t.reversalOfTransactionId && (
                            <ReverseSavingsTransactionDialog
                              transactionId={t.id}
                              onReversed={handleReload}
                            />
                          )}
                          {canReassign && (t.responsibleAgentId ?? t.agentId) != null && (
                            // System-originated rows (auto credit-to-épargne conversion cron,
                            // agentId null) have nothing meaningful to reassign FROM — hidden
                            // rather than showing a confusing "#0".
                            <ReassignResponsibleAgentDialog
                              currentResponsibleAgentId={
                                (t.responsibleAgentId ?? t.agentId) as number
                              }
                              triggerSize="icon"
                              onReassign={(newId, reason) =>
                                savingsApi
                                  .reassignTransactionResponsibleAgent(t.id, newId, reason)
                                  .then(() => handleReload())
                              }
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </QuerySection>
            )}
          </QuerySection>
        </TabsContent>
      </Tabs>

      <EditPartyDialog
        party={editing ? party : null}
        onClose={() => setEditing(false)}
        onUpdated={handleReload}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Supprimer cette partie ?"
        description="Bloqué si des proformas ou factures y sont liées. L'élément reste restaurable depuis la Corbeille."
        confirmLabel="Supprimer"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}
